/* eslint-disable max-len */
import React, { FC, useEffect, useState } from 'react';
import { params, shape, string as stringType } from '@thd-nucleus/data-sources';
import { Typography, Button, Alert } from '@one-thd/sui-atomic-components';
import { useConfigService } from '@thd-nucleus/experience-context';
import { isMobileOnly } from 'react-device-detect';
import { ModalProvider } from '../context/modal-context';
import { DrawerProvider } from '../context/drawer-context';
import { AlertProvider } from '../context/alert-context';
import { GiftCardList } from './list/gift-card-list';
import { ListDropDown } from './list/list-dropdown';
import { FrequentlyAskedQuestions } from './faq/frequently-asked-questions';
import { ScanToPayButton } from './scan-to-pay/scan-to-pay-button';
import { ScanToPayDrawerContainer } from './scan-to-pay/scan-to-pay-drawer-container';
import { B2C_MAX_CARD_LIMIT, PAGE_SIZE, SORT_TYPES, FEATURE_SWITCH_NAMES } from '../utils/consts';
import { getUrlParam, updateUrlParam, scrollToPageTop, isStoreIdInList } from '../utils/general';
import {
  IGiftCardCommonData,
  IPaginationInfo,
  IBalanceProps,
  IListVariables
} from '../models/common-models';
import { ERROR } from '../utils/verbiage';
import { useGiftCardRequest } from '../hooks/use-gift-card-request';
import { useDisableCheckBalanceAndReload } from '../hooks/use-disable-check-balance-and-reload';
import {
  setSavedGiftCardPageLoadEvent,
  setSavedGiftCardPageLoadErrorEvent
} from '../utils/analytics';
import useReCaptcha from '../hooks/use-reCaptcha';
import { useLocationStatus } from '../hooks/use-location-status';

const TechnicalErrorAlert = () => {
  return (
    <Alert data-testid="technical-error" status="error">
      {ERROR.technicalError}
    </Alert>
  );
};

interface SavedGiftCardsListProps {
  onAdd?: () => void;
  isB2B: boolean;
  reCaptchaKey?: string;
}

interface IGetBalanceProps {
  paymentIds: string[];
  recaptcha?: string;
}

interface IScanToPayDrawer {
  isOpen: boolean;
  singleCard?: IGiftCardCommonData | null;
}

const initialScanToPayState: IScanToPayDrawer = {
  isOpen: false,
  singleCard: null
};

const SavedGiftCardsList: FC<SavedGiftCardsListProps> = ({ onAdd, isB2B, reCaptchaKey }) => {
  const [technicalError, setTechnicalError] = useState(false);
  const { isDisableCheckBalance } = useDisableCheckBalanceAndReload({ isB2B });
  const { makeRequest: getList, loading: isListLoading } = useGiftCardRequest({
    queryName: 'giftCards'
  });
  const {
    makeRequest: getBalance,
    loading: isBalancePending,
    error: balanceError
  } = useGiftCardRequest({
    queryName: 'giftCardBalancesByPaymentIds'
  });
  const [paginationInfo, setPaginationInfo] = useState<IPaginationInfo>();
  const [sortBy, setSortBy] = useState('');
  const [giftCardList, setGiftCardList] = useState<IGiftCardCommonData[]>([]);
  const total = paginationInfo?.totalNumberOfRecords || 0;
  const [balanceList, setBalanceList] = useState(Array<IBalanceProps>);

  const isBalanceLoading = isBalancePending
    || (giftCardList.length && !balanceList.length && !balanceError && !isDisableCheckBalance);
  const reCaptchaEnabled = !!useConfigService(FEATURE_SWITCH_NAMES.reCaptchaEnabled);

  // Start - Scan to Pay
  const [isScanToPayDrawerOpen, setIsScanToPayDrawerOpen] = useState<IScanToPayDrawer>(initialScanToPayState);
  const isScanToPayEnabledForB2C = !!useConfigService(FEATURE_SWITCH_NAMES.ENABLE_SCAN_TO_PAY_B2C) && !isB2B;
  const isScanToPayEnabledForB2B = !!useConfigService(FEATURE_SWITCH_NAMES.ENABLE_SCAN_TO_PAY_B2B) && isB2B;
  const isScanToPayEnabled = (isScanToPayEnabledForB2B || isScanToPayEnabledForB2C) && isMobileOnly;
  // Get store ID list from feature switch and check if current store is allowed
  const storeIdList = useConfigService(FEATURE_SWITCH_NAMES.SCAN_TO_PAY_STORE_ID_LIST) as string;
  // If no store ID list is configured, default to false (opt-out).
  // When a list is provided, check membership via isStoreIdInList.
  const isStoreAllowed = storeIdList ? isStoreIdInList(storeIdList) : false;
  const { isUserInStore } = useLocationStatus();
  // Feature switch to control whether user must be in store
  const isInStoreRequired = !!useConfigService(FEATURE_SWITCH_NAMES.SCAN_TO_PAY_REQUIRE_IN_STORE);

  // Separate flag for showing Scan to Pay inside the meatball menu (no total check)
  // If isInStoreRequired is true, user must be in store; otherwise location doesn't matter
  const showScanToPayButtonInMeatballMenu = isScanToPayEnabled && isStoreAllowed && (!isInStoreRequired || isUserInStore);
  const shouldShowScanToPayButton = showScanToPayButtonInMeatballMenu && total > 0;
  const scanToPayAllStoresEnabled = showScanToPayButtonInMeatballMenu && storeIdList.trim().toUpperCase() === 'ALL_STORES';

  const handleScanToPayClick = async (paymentId?: string) => {
    let selectedSingleCard = null;
    if (paymentId) {
      selectedSingleCard = giftCardList.find((card) => card.id === paymentId) || null;
      const availableBalance = balanceList?.[paymentId]?.availableBalance;
      if (selectedSingleCard && availableBalance) {
        selectedSingleCard = { ...selectedSingleCard, availableBalance };
      }
    }
    setIsScanToPayDrawerOpen({
      isOpen: true,
      singleCard: selectedSingleCard || null
    });
  };

  const handleCloseScanToPayDrawerContainer = () => {
    setIsScanToPayDrawerOpen({
      isOpen: false,
      singleCard: null
    });
  };
  // End - Scan to Pay

  const handleAdd = () => {
    if (onAdd) {
      onAdd();
    }
  };

  const handleGetBalance = (token: string, gcList: IGiftCardCommonData[] = []) => {
    if (isDisableCheckBalance) {
      setBalanceList([]);
      return;
    }
    const getBalanceVariables: IGetBalanceProps = {
      paymentIds: gcList.map((card: IGiftCardCommonData) => card.id)
    };
    if (reCaptchaEnabled && token) {
      getBalanceVariables.recaptcha = token;
    }
    getBalance(getBalanceVariables)
      .then((data) => {
        const balances = data?.paymentCards?.reduce(
          (obj: IBalanceProps, cur: { id: string; availableBalance?: number }) => {
            return { ...obj, [cur.id]: cur };
          },
          {}
        );
        setTimeout(() => setBalanceList(balances));
      })
      .catch(() => {
        setBalanceList([]);
      });
  };

  const { getToken } = useReCaptcha({
    onSubmit: handleGetBalance,
    siteKey: reCaptchaKey,
    action: 'Balance_Check_GC'
  });

  const handleListChange = (
    _paginationInfo?: { pageNumber?: string },
    _sortInfo?: { sortBy?: string; asc?: boolean },
    isInit?: boolean
  ) => {
    scrollToPageTop();
    const variables: IListVariables = {
      paginate: { pageSize: PAGE_SIZE.toString(), pageNumber: '1' }
    };

    if (_paginationInfo) {
      variables.paginate = {
        ...variables.paginate,
        ..._paginationInfo
      };
    }
    if (_sortInfo) {
      variables.sort = _sortInfo;
    } else if (sortBy) {
      variables.sort = SORT_TYPES[sortBy];
    }
    getList(variables)
      .then((_data) => {
        if (_data?.paymentCards) {
          setGiftCardList(() => _data.paymentCards);
          const nonLegacyCards = _data.paymentCards.filter(
            (card: IGiftCardCommonData) => !card.actions?.isLegacyCard
          );
          if (nonLegacyCards.length) {
            if (reCaptchaEnabled) {
              getToken(nonLegacyCards);
            } else {
              handleGetBalance('', nonLegacyCards);
            }
          } else {
            setBalanceList([]);
          }
        }

        if (_data?.paginationInfo) {
          const totalNumberOfRecords = parseInt(_data.paginationInfo.totalNumberOfRecords, 10);
          setPaginationInfo({
            pageNumber: parseInt(_data.paginationInfo.pageNumber, 10),
            pageSize: parseInt(_data.paginationInfo.pageSize, 10),
            totalPages: parseInt(_data.paginationInfo.totalPages, 10),
            totalNumberOfRecords
          });

          if (isInit) {
            setSavedGiftCardPageLoadEvent(totalNumberOfRecords.toString());
          }
        }
      })
      .catch(() => {
        handleTechnicalError();
        if (isInit) {
          setSavedGiftCardPageLoadErrorEvent(ERROR.technicalError);
        }
      });
  };

  const handleSortChange = (_sortBy: string) => {
    if (!_sortBy) {
      return;
    }
    updateUrlParam([{ key: 'page', newValue: null }]);
    const _paginationInfo = {
      pageNumber: '1'
    };
    setSortBy(_sortBy);
    handleListChange(_paginationInfo, SORT_TYPES[_sortBy]);
  };

  const handleDelete = (isLastCardOnPage: boolean) => {
    let currentPage = paginationInfo?.pageNumber || 1;
    if (isLastCardOnPage && currentPage > 1) {
      currentPage -= 1;
      updateUrlParam([
        { key: 'page', newValue: currentPage === 1 ? null : currentPage.toString() }
      ]);
    }
    const _paginationInfo = {
      pageNumber: currentPage.toString()
    };
    handleListChange(_paginationInfo);
  };

  const handleTechnicalError = () => {
    setGiftCardList([]);
    setTechnicalError(true);
  };

  useEffect(() => {
    let initPagiationInfo;
    let initSortInfo;

    const currentPage = getUrlParam('page');
    if (currentPage) {
      initPagiationInfo = {
        pageNumber: currentPage
      };
    }
    const _sortBy = getUrlParam('sortBy');
    if (_sortBy) {
      const sortName: string = _sortBy.replaceAll('_', ' ');
      initSortInfo = SORT_TYPES[sortName];
      setSortBy(sortName);
    }
    handleListChange(initPagiationInfo, initSortInfo, true);
  }, []);

  const renderSort = () => {
    return (
      total > PAGE_SIZE && (
        <div
          className="
        sui-mb-4 sm:sui-mb-8 sui-grid sui-grid-cols-1 sm:sui-grid-cols-2 sui-gap-4 lg:sui-grid-cols-3 sui-gap-8
        "
        >
          <ListDropDown
            sortBy={sortBy}
            disabled={isListLoading}
            onSortChange={handleSortChange}
            isB2B={isB2B}
          />
        </div>
      )
    );
  };

  if (technicalError) {
    return <TechnicalErrorAlert />;
  }

  return (
    <>
      {shouldShowScanToPayButton && (
        <ScanToPayDrawerContainer
          isOpen={isScanToPayDrawerOpen.isOpen}
          onClose={handleCloseScanToPayDrawerContainer}
          singleCard={isScanToPayDrawerOpen?.singleCard}
        />
      )}
      <ModalProvider>
        <DrawerProvider>
          <div className="sui-w-full" data-component="SavedGiftCardsList">
            <div
              className="
          sui-grid
          sui-grid-cols-1
          md:sui-grid-cols-6
          sui-gap-y-6
          md:sui-gap-x-8
          md:sui-gap-y-6
          sui-mb-6
          md:sui-mb-6
          sui-items-start
          sui-items-top
        "
            >
              <div className="sui-order-3 md:sui-order-1 sui-col-span-1 md:sui-col-span-3">
                <Typography
                  weight="display"
                  variant="h2"
                  uppercase
                  data-testid="gift-card-page-header"
                  id="savedGiftCardsHeader"
                >
                  {`Gift Cards (${total})`}
                </Typography>
              </div>
              <div className="sui-order-1 md:sui-order-3 sui-col-span-1 md:sui-col-span-2 md:sui-col-start-5">
                <Button
                  data-testid="gift-card-add"
                  fullWidth
                  onClick={handleAdd}
                  disabled={!isB2B && total >= B2C_MAX_CARD_LIMIT}
                >
                  Add Gift Card
                </Button>
              </div>
            </div>
            <div className="sui-w-full sui-flex sui-justify-end sui-mb-6">
              <FrequentlyAskedQuestions />
            </div>
            {isDisableCheckBalance && (
              <Alert
                status="warning"
                headline={ERROR.serviceUnavailable}
                data-testid="disable-check-balance"
              >
                {ERROR.disableCheckBalance}
              </Alert>
            )}
            <AlertProvider>
              <>
                {shouldShowScanToPayButton && <ScanToPayButton onClick={handleScanToPayClick} />}
                {renderSort()}
                <GiftCardList
                  giftCards={giftCardList}
                  balances={balanceList}
                  isListLoading={isListLoading}
                  isBalanceLoading={isBalanceLoading}
                  isB2B={isB2B}
                  paginationInfo={paginationInfo}
                  balanceError={balanceError}
                  onListChange={handleListChange}
                  onDeleteGiftCard={handleDelete}
                  showScanToPayButton={showScanToPayButtonInMeatballMenu}
                  scanToPayAllStoresEnabled={scanToPayAllStoresEnabled}
                  onScanToPay={handleScanToPayClick}
                />
              </>
            </AlertProvider>
          </div>
        </DrawerProvider>
      </ModalProvider>
    </>
  );
};

SavedGiftCardsList.displayName = 'SavedGiftCardsList';
SavedGiftCardsList.dataModel = {
  product: params({ itemId: stringType().isRequired() }).shape({
    itemId: stringType(),
    dataSource: stringType(),
    identifiers: shape({
      productLabel: stringType()
    })
  })
};

export { SavedGiftCardsList };
